import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import { useNavigate, useLocation } from "react-router-dom";
import StripePaymentElement from "../../common/component/StripePaymentElement";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  getDefaultQuoteExpiration,
  validateCustomer,
  validateFulfillment,
  buildPaymentForOrderApi,
  buildInvoiceQuoteFulfillment,
  getDefaultBalanceDueDate,
  isSalesTaxEnabled,
  shouldCollectSalesTax,
} from "../../common/util";
import CustomerDetailEntry from "../../customer/component/CustomerDetailEntry";
import PaymentDue from "../component/PaymentDue";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import FulfillmentEntry from "../../common/component/fulfillment/FulfillmentEntry";
import moment from "moment";
import { UserContext } from "../../context/UserContext";
import LineItems from "../../common/component/lineitem/LineItems";
import BDatePicker from "../../common/input/BDatePicker";
import PaymentStatus from "../../common/screen/PaymentStatus";
import PaymentEntry from "../component/PaymentEntry";
import SameDayPaymentDueWarning from "../component/SameDayPaymentDueWarning";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import EmailCCInput from "../../common/component/EmailCCInput";

const key = process.env.REACT_APP_STRIPE_KEY;
const promise = loadStripe(key);

export default function CreateOrder(props) {
  const navigate = useNavigate();
  let location = useLocation();

  const { user } = useContext(UserContext);

  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState("");
  const [fulfillment, setFulfillment] = useState({ type: "pickup" });
  const [adjustments, setAdjustments] = useState({});
  const [reqId, setReqId] = useState("");

  const [chargeType, setChargeType] = useState("now");
  const [paymentMethod, setPaymentMethod] = useState(user.config?.readerId ? "reader" : "keyed");
  const [acceptDeposit, setAcceptDeposit] = useState(false);
  const [deposit, setDeposit] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [payment, setPayment] = useState("");
  const [collectSalesTax, setCollectSalesTax] = useState(isSalesTaxEnabled(user.config));

  const [mode, setMode] = useState("itemized");
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const tabsRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (location.state) {
      setItems(location.state.items);

      if (location.state.fulfillment) {
        setFulfillment({ ...fulfillment, ...location.state.fulfillment });
      }
      if (location.state.adjustments) {
        setAdjustments(location.state.adjustments);
      }
      setReqId(location.state.reqId);

      if (
        location.state.settings?.collectSalesTax !== undefined ||
        location.state.settings?.includeSalesTax !== undefined
      ) {
        setCollectSalesTax(shouldCollectSalesTax(location.state));
      }

      let o = location.state.customer;
      let oCust = {
        firstName: o.firstName,
        lastName: o.lastName || "",
        phone: o.phone || "",
        email: o.email || "",
        email_cc: o.email_cc || "",
        loyaltyLevel: o.loyaltyLevel || "",
      };
      if (o.type === "commercial") {
        oCust.type = o.type;
        oCust.paymentTerms = o.paymentTerms;
      }
      handleCustomerChange((o && oCust) || "", location.state.chargeType);
    }
  }, []);

  // Check URL parameters for mode
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlMode = urlParams.get("mode");
    if (urlMode === "quick") {
      setMode("quick");
      // Scroll to tabs component after a short delay to ensure it's rendered
      setTimeout(() => {
        tabsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.search]);

  const validate = () => {
    let arr = [];
    console.log("validate", fulfillment);
    if (items.length === 0) {
      arr.push("Items are missing");
    }
    arr = arr.concat(validateFulfillment(fulfillment));
    arr = arr.concat(validateCustomer(customer));
    if (acceptDeposit) {
      if (!deposit) {
        arr.push("Deposit amount is missing");
      }
      if (!dueDate) {
        arr.push("Balance due date is missing");
      }
    }

    return arr;
  };

  useEffect(() => {
    setDeposit("");
    setDueDate(getDefaultBalanceDueDate(fulfillment?.date));
  }, [acceptDeposit]);

  useEffect(() => {
    if (chargeType === "now") {
      setPaymentMethod(user.config?.readerId ? "reader" : "keyed");
      setAcceptDeposit(false);
    } else {
      setPaymentMethod("");
      setAcceptDeposit(false);
    }
  }, [chargeType]);

  useEffect(() => {
    if (fulfillment?.date) {
      setAcceptDeposit(false);
      setDeposit("");
      setDueDate("");

      handleCustomerChange(customer, chargeType);
    }
  }, [fulfillment?.date]);

  const handleCustomerChange = (o, ochargeTyp) => {
    setCustomer(o);
    if (o.type === "commercial") {
      setChargeType("deferred");
      const netDays = o.paymentTerms.replace(/\D/g, "") || 30;
      if (fulfillment.date) {
        setDueDate(moment(fulfillment.date).add(netDays, "days").format("YYYY-MM-DD"));
      }
    } else {
      setChargeType(ochargeTyp || "now");
    }
  };

  const chargePaymentMethod = (paymentMethodId, payment) => {
    setLoading(true);

    const payload = {
      payment: buildPaymentForOrderApi(payment, dueDate),
      items,
      fulfillment,
      customer,
      paymentMethodId,
      reqId,
      settings: { collectSalesTax },
    };

    bkstApi
      .post(`/my/payment`, payload)
      .then((res) => {
        navigate(`/order/${res.data.orderId}`);
      })
      .catch((e) => {
        setErrors([`Payment Failed: ${e.response?.data?.message}`]);
        setLoading(false);
      });
  };

  const sendToCardReader = () => {
    const arr = validate();
    setErrors(arr);

    if (arr.length === 0) {
      const payload = {
        payment: buildPaymentForOrderApi(payment, dueDate),
        items,
        fulfillment,
        customer,
        reqId,
        settings: { collectSalesTax },
      };

      return bkstApi.post(`/my/reader/${user.config.readerId}`, payload);
    }
  };

  const createOfflineOrder = () => {
    const arr = validate();

    if (arr.length > 0) {
      setErrors(arr);
    } else {
      setLoading(true);

      const payload = {
        payment: buildPaymentForOrderApi(payment, dueDate),
        items,
        fulfillment,
        customer,
        reqId,
        receiptPrinterId: user.config?.receiptPrinterId || "",
        settings: { collectSalesTax },
      };

      bkstApi
        .post(`/my/order`, payload)
        .then((res) => {
          navigate(`/order/${res.data.orderId}`);
        })
        .catch((e) => {
          setErrors([`Error: ${e.response?.data?.message}`]);
          setLoading(false);
        });
    }
  };

  const sendInvoice = (mode) => {
    const arr = validate();

    if (arr.length > 0) {
      setErrors(arr);
    } else {
      setLoading(true);

      const payload = {
        items,
        fulfillment: buildInvoiceQuoteFulfillment(fulfillment),
        customer,
        settings: { collectSalesTax },
        exdate: getDefaultQuoteExpiration(fulfillment.date),
        mode: mode || "send",
      };
      if (+payment?.discount > 0) {
        if (!payload.adjustments) {
          payload.adjustments = {};
        }
        payload.adjustments.discount = {
          description: "Merchant Discount",
          value: +payment?.discount,
          type: "manual",
        };
      }
      console.log("payload", payload);
      bkstApi
        .post(`/my/quote`, payload)
        .then((res) => {
          navigate(`/quote/${res.data.quoteId}`);
        })
        .catch((e) => {
          setErrors([`Error: ${e.response?.data?.message}`]);
          setLoading(false);
        });
    }
  };

  const createDeferredPaymentOrder = () => {
    const arr = validate();

    if (arr.length > 0) {
      setErrors(arr);
    } else {
      setLoading(true);

      const payload = {
        paymentMethod: "deferred",
        payment: buildPaymentForOrderApi(payment, dueDate),
        items,
        fulfillment,
        customer,
        reqId,
        settings: { collectSalesTax },
      };

      bkstApi
        .post(`/my/order`, payload)
        .then((res) => {
          navigate(`/order/${res.data.orderId}`);
        })
        .catch((e) => {
          setErrors([`Error: ${e.response?.data?.message}`]);
          setLoading(false);
        });
    }
  };

  console.log("RENDER >>>>");

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={30}>
        {location?.state?.chargeType === "invoice" ? (
          <Box mb={2}>
            <Typography variant="h1" gutterBottom>
              Send Invoice
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Use this for quotes requested via phone, email, social media, or in-store. Includes
              automated follow-ups, balance payment, reviews and more!
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box mb={2}>
              <Typography variant="h1" gutterBottom>
                Create Order
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Accept payment for in-store & phone orders
              </Typography>
            </Box>
          </Box>
        )}
        {mode === "itemized" && (
          <Box>
            <Box my={2}>
              {location?.state?.items && location?.state?.items?.length > 0 && (
                <Alert severity="warning">
                  The items below have been transfered over from another screen. Be sure to edit
                  items below to ensure all required options have been specified.
                </Alert>
              )}
            </Box>
            <LineItems items={items} onChange={(arr) => setItems(arr)} />
            {items.length > 0 && (
              <Box>
                <Box>
                  {(customer || items.length > 0) && (
                    <Box>
                      <Box my={4}></Box>
                      <Typography variant="overline" color="textSecondary">
                        CUSTOMER
                      </Typography>
                      <Paper elevation={0}>
                        <Box px={2} py={2}>
                          <CustomerDetailEntry
                            customer={customer}
                            onChange={(o) => {
                              handleCustomerChange(o);
                            }}
                            mode={location.state?.customer ? "view" : "edit"}
                          />
                        </Box>
                      </Paper>
                      {chargeType !== "deferred" && (
                        <Box>
                          <EmailCCInput
                            value={customer?.email_cc || ""}
                            onChange={(val) => {
                              handleCustomerChange({ ...customer, email_cc: val });
                            }}
                            label="CC Email"
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                  {items.length > 0 && (
                    <Box>
                      <Box my={4}></Box>
                      <Typography variant="overline" color="textSecondary">
                        SHIPPING
                      </Typography>
                      <Paper elevation={0} sx={{ minWidth: 0 }}>
                        <Box px={2} pt={2} pb={2} sx={{ minWidth: 0 }}>
                          <FulfillmentEntry
                            fulfillment={fulfillment}
                            onChange={(o) => setFulfillment(o)}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  )}
                  {items.length > 0 && (
                    <Box>
                      <Box my={4}></Box>
                      <Typography variant="overline" color="textSecondary">
                        PAYMENT SUMMARY
                      </Typography>
                      <Paper elevation={0}>
                        <Box p={2}>
                          <PaymentEntry
                            items={items}
                            fulfillment={fulfillment}
                            adjustments={adjustments}
                            payment={payment}
                            acceptDeposit={acceptDeposit}
                            deposit={deposit}
                            paymentMethod={paymentMethod}
                            collectSalesTax={collectSalesTax}
                            onCollectSalesTaxChange={setCollectSalesTax}
                            onChange={(o) => setPayment(o)}
                          />
                          <Box pt={1} mt={2} sx={{ borderTop: "1px solid #EEE" }}>
                            <PaymentDue
                              total={payment.total}
                              due={payment.due}
                              date={fulfillment.date}
                            />
                          </Box>
                        </Box>
                      </Paper>

                      <Box my={4}></Box>
                      {customer.type !== "commercial" && (
                        <Tabs value={chargeType} variant="scrollable" scrollButtons="auto">
                          <Tab
                            value="now"
                            label="Charge Now"
                            onClick={() => setChargeType("now")}
                          />
                          <Tab
                            value="invoice"
                            label="Send Invoice"
                            onClick={() => setChargeType("invoice")}
                          />
                        </Tabs>
                      )}
                      {chargeType === "invoice" && (
                        <Box>
                          <Paper elevation={0}>
                            <Box px={2} py={3}>
                              <Typography variant="h6" gutterBottom>
                                How it works?
                              </Typography>
                              <Typography variant="body1" color="textSecondary">
                                Customer will be notified via text & email. The order will be
                                confirmed when the customer pays for the order.
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}
                      {chargeType === "deferred" && (
                        <Box>
                          <Paper elevation={0}>
                            <Box px={2} py={3}>
                              <Box mb={2}>
                                <Typography variant="h6" gutterBottom>
                                  Commercial Account
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                  You're creating an order for a commercial account. Payment will be
                                  due after the order is fulfilled.
                                </Typography>
                              </Box>
                              <BDatePicker
                                label="Payment Due Date"
                                value={dueDate}
                                onChange={(d) => setDueDate(d)}
                                disablePast
                                minDate={moment(fulfillment.date, "YYYY-MM-DD").toDate()}
                                required
                                autoOk
                              />
                            </Box>
                          </Paper>
                        </Box>
                      )}
                      {chargeType === "now" && (
                        <Box>
                          <Paper elevation={0}>
                            <Box p={2} pt={4}>
                              <Grid container spacing={3}>
                                <Grid item xs={6}>
                                  <FormControl variant="outlined" fullWidth>
                                    <InputLabel id="paymentMethod-label">Payment Method</InputLabel>
                                    <Select
                                      value={paymentMethod}
                                      labelId="paymentMethod-label"
                                      label="Payment Method"
                                      id="paymentMethod"
                                      name="paymentMethod"
                                      onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                      {user.config?.readerId && (
                                        <MenuItem value="reader">Card Reader</MenuItem>
                                      )}
                                      <MenuItem value="keyed">Keyed (manually enter card)</MenuItem>
                                      <MenuItem
                                        value="cash"
                                        disabled={
                                          !(user.plan === "professional" || user.plan === "premium")
                                        }
                                      >
                                        Cash
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                  <FormControl variant="outlined" fullWidth>
                                    <InputLabel id="acceptDeposit-label">Payment Type</InputLabel>
                                    <Select
                                      value={acceptDeposit}
                                      labelId="acceptDeposit-label"
                                      label="Payment Type"
                                      id="acceptDeposit"
                                      name="acceptDeposit"
                                      onChange={(e) => setAcceptDeposit(e.target.value)}
                                    >
                                      <MenuItem value={false}>Charge in Full</MenuItem>
                                      <MenuItem
                                        value={true}
                                        disabled={
                                          !fulfillment.date ||
                                          !["pickup", "delivery"].includes(fulfillment.type)
                                        }
                                      >
                                        Charge a Deposit
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                {acceptDeposit && (
                                  <Grid item xs={6}>
                                    <TextField
                                      id="deposit"
                                      type="number"
                                      value={deposit}
                                      label="Deposit Amount"
                                      name="deposit"
                                      variant="outlined"
                                      fullWidth
                                      required
                                      onChange={(e) => setDeposit(e.target.value)}
                                      onWheel={(e) => {
                                        e.target && e.target.blur();
                                      }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <AttachMoneyOutlinedIcon />
                                          </InputAdornment>
                                        ),
                                      }}
                                      error={+deposit >= +payment.total}
                                      helperText={
                                        +deposit >= +payment.total
                                          ? "Deposit amount should be less than total amount"
                                          : ""
                                      }
                                    />
                                  </Grid>
                                )}
                                {acceptDeposit && (
                                  <Grid item xs={6}>
                                    <FormControl variant="outlined" fullWidth>
                                      <BDatePicker
                                        label="Balance Due Date"
                                        value={dueDate}
                                        onChange={(d) => setDueDate(d)}
                                        disablePast
                                        maxDate={
                                          fulfillment.date
                                            ? moment(fulfillment.date, "YYYY-MM-DD").toDate()
                                            : undefined
                                        }
                                        required
                                        autoOk
                                      />
                                    </FormControl>
                                  </Grid>
                                )}
                              </Grid>
                              {acceptDeposit && (
                                <SameDayPaymentDueWarning
                                  dueDate={dueDate}
                                  fulfillmentDate={fulfillment.date}
                                  customerType={customer?.type}
                                />
                              )}
                            </Box>
                          </Paper>
                        </Box>
                      )}
                      {errors && errors.length > 0 && (
                        <Box my={2}>
                          <Alert severity="error">{errors.join(". ")}</Alert>
                        </Box>
                      )}
                      <Box my={2}>
                        {paymentMethod === "keyed" && (
                          <Box>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                              Enter the customer's card details below to confirm this order
                            </Typography>
                            <Elements stripe={promise}>
                              <StripePaymentElement
                                validate={validate}
                                chargePaymentMethod={(paymentMethodId) =>
                                  chargePaymentMethod(paymentMethodId, payment)
                                }
                                amount={payment.due}
                                name={customer.name}
                                phone={customer.phone}
                              />
                            </Elements>
                          </Box>
                        )}
                        {paymentMethod === "cash" && (
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={createOfflineOrder}
                            fullWidth
                          >
                            CREATE CASH ORDER
                          </Button>
                        )}
                        {chargeType === "invoice" && (
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}
                          >
                            <Button
                              color="secondary"
                              variant="contained"
                              onClick={() => sendInvoice("save")}
                              fullWidth
                            >
                              SAVE DRAFT
                            </Button>
                            <Button
                              color="primary"
                              variant="contained"
                              onClick={() => sendInvoice("send")}
                              fullWidth
                            >
                              SEND INVOICE
                            </Button>
                          </Box>
                        )}
                        {chargeType === "deferred" && (
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={createDeferredPaymentOrder}
                            fullWidth
                          >
                            CREATE DEFERRED PAYMENT ORDER
                          </Button>
                        )}
                        {paymentMethod === "reader" && (
                          <Box my={4}>
                            <PaymentStatus
                              amount={payment.due}
                              onSubmit={sendToCardReader}
                              readerId={user.config.readerId}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Backdrop open={loading} style={{ zIndex: "1000", color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}
